import { WS_URL } from '../utils/constants.js';

export class WebSocketClient {
  constructor(onMessage) {
    this.ws = null;
    this.onMessage = onMessage;
    this.onConnectionChange = null;
    this.reconnectAttempts = 0;
    this.baseReconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.isIntentionallyClosed = false;
    this.reconnectTimeout = null;
  }

  connect() {
    this.isIntentionallyClosed = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.ws = new WebSocket(WS_URL);

    this.ws.onopen = () => {
      console.log('connected');
      this.reconnectAttempts = 0;
      this.onConnectionChange?.(true);
    };

    this.ws.onmessage = (event) => {
      // Server's WritePump coalesces multiple queued messages into one
      // WebSocket frame separated by '\n'. We have to split on newline and
      // parse each line as its own JSON document — calling JSON.parse on
      // the whole frame fails the moment more than one message is queued,
      // which manifests as parse errors mid-race and a dropped connection.
      const data = typeof event.data === 'string' ? event.data : '';
      for (const line of data.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        let message;
        try {
          message = JSON.parse(trimmed);
        } catch (e) {
          console.error('parse error:', e.message, 'line:', trimmed.slice(0, 200));
          continue;
        }
        this.onMessage?.(message);
      }
    };

    this.ws.onerror = (err) => console.error('ws error:', err);

    this.ws.onclose = () => {
      console.log('disconnected');
      this.onConnectionChange?.(false);
      if (!this.isIntentionallyClosed) {
        this.reconnectAttempts++;
        const delay = Math.min(
          this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
          this.maxReconnectDelay,
        );
        console.log(`reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
        this.reconnectTimeout = setTimeout(() => this.connect(), delay);
      }
    };
  }

  send(type, payload = {}) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, payload }));
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  setConnectionChangeHandler(handler) {
    this.onConnectionChange = handler;
  }

  close() {
    this.isIntentionallyClosed = true;
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    if (this.ws) this.ws.close();
  }
}
